import React from 'react';
import { Select, Text, TextField, formatSandboxActionParams, type SelectOption } from '@chatsdet/ui';
import { MacroRichTextField } from './MacroRichTextField';
import { ConfigSection } from './ConfigSection';
import { CopyablePre } from '../CopyButton';
import { useAvailableSandboxActions } from '../../../hooks/useAvailableSandboxActions';

type SandboxActionNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

/**
 * Config panel for sandbox_action nodes.
 *
 * Picks an action declared by the org's Sandboxes v2 definitions (or an
 * emulator operation) and renders a param form generated from its input.
 * Selecting an action stamps `actionSlug` and denormalizes
 * `outputKeys` from its output (design-time variable production reads them
 * from node config). Param values support `$var.*` / macro tokens, resolved
 * at run time. The stored params are also shown read-only as the code the
 * action runs (e.g. the SQL statement).
 */
export const SandboxActionNodeConfig: React.FC<SandboxActionNodeConfigProps> = ({
  localData,
  setLocalData,
}) => {
  const { actions, isLoading } = useAvailableSandboxActions();

  const actionSlug = (localData.actionSlug as string) ?? '';
  const params: Record<string, string> =
    localData.params && typeof localData.params === 'object'
      ? (localData.params as Record<string, string>)
      : {};
  const outputKeys: string[] = Array.isArray(localData.outputKeys)
    ? (localData.outputKeys as string[])
    : [];
  const title = typeof localData.title === 'string' ? localData.title : '';
  const description = typeof localData.description === 'string' ? localData.description : '';

  const selected = actions.find((action) => action.slug === actionSlug) ?? null;

  const actionOptions: SelectOption[] = React.useMemo(
    () =>
      actions.map((action) => ({
        id: action.slug,
        label: action.slug,
        description: action.description,
        tags: action.kind === 'operation' ? [{ label: 'Emulator' }] : undefined,
      })),
    [actions]
  );

  const handleSelectAction = (slug: string) => {
    const action = actions.find((candidate) => candidate.slug === slug);
    // Seed params with the schema's string defaults so what the form shows is
    // what gets persisted — otherwise an author accepting a displayed default
    // without editing would save a node missing that parameter.
    const defaultParams: Record<string, string> = {};
    for (const [key, prop] of Object.entries(action?.inputSchema.properties ?? {})) {
      const d = prop.default;
      // Params are strings end-to-end; the runtime coerces back per inputSchema.
      if (typeof d === 'string' && d.length > 0) {
        defaultParams[key] = d;
      } else if (typeof d === 'number' || typeof d === 'boolean') {
        defaultParams[key] = String(d);
      }
    }
    setLocalData((prev) => ({
      ...prev,
      actionSlug: slug,
      outputKeys: action?.outputKeys ?? [],
      params: defaultParams,
    }));
  };

  const setParam = (key: string, value: string) =>
    setLocalData((prev) => {
      const prevParams =
        prev.params && typeof prev.params === 'object'
          ? { ...(prev.params as Record<string, string>) }
          : {};
      if (value.length === 0) {
        delete prevParams[key];
      } else {
        prevParams[key] = value;
      }
      return { ...prev, params: prevParams };
    });

  const inputProperties = selected?.inputSchema?.properties ?? {};
  const requiredInputs = new Set(selected?.inputSchema?.required ?? []);

  // What the action runs: the stored params, sensitive ones masked.
  const paramsText = formatSandboxActionParams(params, (key) => inputProperties[key]?.sensitive === true);

  return (
    <div className="space-y-4">
      {(title || description) && (
        <div className="space-y-1">
          {title && (
            <Text size="sm" weight="medium">
              {title}
            </Text>
          )}
          {description && (
            <Text size="sm" className="text-muted-foreground">
              {description}
            </Text>
          )}
        </div>
      )}

      {actionOptions.length === 0 ? (
        <Text size="sm" className="text-muted-foreground">
          {isLoading
            ? 'Loading sandbox actions…'
            : 'No sandbox actions found. Declare actions in an application sandbox definition first.'}
        </Text>
      ) : (
        <Select
          label="Sandbox action"
          items={actionOptions}
          selectedKey={actionSlug || null}
          placeholder="Choose an action…"
          onSelectionChange={(key) => handleSelectAction(String(key))}
        />
      )}

      {/* A slug no definition declares (e.g. a node left over from before Sandboxes v2). */}
      {actionSlug && !selected && !isLoading && actionOptions.length > 0 && (
        <Text size="sm" className="text-amber-600">
          No sandbox definition declares the action '{actionSlug}'. The run environment's
          definition must declare it.
        </Text>
      )}

      {paramsText !== null && (
        <ConfigSection title="Runs" count={Object.keys(params).length}>
          <CopyablePre value={paramsText} label="action">
            <pre className="text-xs font-mono bg-background p-2 rounded border border-border overflow-x-auto max-h-64 whitespace-pre-wrap break-words">
              {paramsText}
            </pre>
          </CopyablePre>
        </ConfigSection>
      )}

      {selected && Object.keys(inputProperties).length > 0 && (
        <ConfigSection title="Inputs" count={Object.keys(inputProperties).length}>
          <div className="space-y-3">
            {Object.entries(inputProperties).map(([key, prop]) =>
              prop.sensitive ? (
                // Sensitive inputs render masked (mirrors ActionExecuteDialog).
                // Prefer $var.* references over hardcoding secrets — the value
                // is stored in node config.
                <TextField
                  key={key}
                  type="password"
                  label={`${key}${requiredInputs.has(key) ? ' *' : ''}`}
                  placeholder="******** (prefer a $var.* reference)"
                  value={params[key] ?? ''}
                  onChange={(v) => setParam(key, v)}
                />
              ) : (
                <MacroRichTextField
                  key={key}
                  label={`${key}${requiredInputs.has(key) ? ' *' : ''}`}
                  placeholder={prop.description || `${prop.type}… (type $ for macros)`}
                  // Render ONLY persisted values — defaults are seeded into
                  // params at action-select time, never displayed unsaved.
                  value={params[key] ?? ''}
                  onChange={(v) => setParam(key, v)}
                />
              )
            )}
          </div>
        </ConfigSection>
      )}

      {outputKeys.length > 0 && (
        <ConfigSection title="Outputs" count={outputKeys.length}>
          <div className="flex flex-wrap gap-1.5">
            {outputKeys.map((key) => (
              <span
                key={key}
                className="px-2 py-0.5 rounded bg-muted font-mono text-xs text-muted-foreground"
              >
                $var.{key}
              </span>
            ))}
          </div>
          <Text size="sm" className="text-muted-foreground mt-2">
            Available to downstream steps after this action completes.
          </Text>
        </ConfigSection>
      )}
    </div>
  );
};
